import type { ReactNode } from 'react';
import { Bar, BarChart, CartesianGrid, LabelList, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { AnalyticsTable } from '../../services/analytics';
import { DataTable } from './DataTable';
import { formatInt, formatPct } from './format';
import { InfoTip } from './InfoTip';
import type { DistributionBar, GroupRow, GroupSeries } from './mappingCards';
import { METRIC_INFO, type MetricInfoKey } from './metricInfo';
import styles from './SurveyCharts.module.css';

const ROW_HEIGHT = 34;
const AXIS_HEIGHT = 28;
const SERIES_COLORS = ['var(--color-primary-600)', 'var(--color-primary-300)', 'var(--color-gray-400)'];

const TOOLTIP_STYLE = {
    background: 'var(--color-white)',
    border: '1px solid var(--color-border-light)',
    borderRadius: 8,
    fontSize: 12,
};

interface ChartCardProps {
    title: string;
    infoKey: MetricInfoKey;
    subtitle: string;
    table: AnalyticsTable;
    formatters?: Record<string, (value: unknown) => string>;
    exportFilename: string;
    timeZone: string;
    wide?: boolean;
    children: ReactNode;
}

function ChartCard({ title, infoKey, subtitle, table, formatters, exportFilename, timeZone, wide, children }: ChartCardProps) {
    const rows = table.rows.length;
    return (
        <section className={`${styles.card} ${wide ? styles.wide : ''}`}>
            <header>
                <h4 className={styles.title}>
                    {title}
                    <InfoTip info={METRIC_INFO[infoKey]} label={title} align="start" />
                </h4>
                <p className={styles.subtitle}>{subtitle}</p>
            </header>
            {rows === 0 ? <p className={styles.empty}>Nenhuma resposta no período.</p> : children}
            {rows > 0 && (
                <details className={styles.details}>
                    <summary className={styles.summary}>
                        <span>Ver dados e exportar</span>
                        <span className={styles.count}>
                            {formatInt(rows)} {rows === 1 ? 'linha' : 'linhas'}
                        </span>
                    </summary>
                    <DataTable
                        title={title}
                        table={table}
                        formatters={formatters}
                        loading={false}
                        error={null}
                        onRetry={() => undefined}
                        timeZone={timeZone}
                        emptyMessage="Nenhuma resposta no período."
                        exportFilename={exportFilename}
                    />
                </details>
            )}
        </section>
    );
}

interface DistributionChartProps {
    title: string;
    infoKey: MetricInfoKey;
    bars: DistributionBar[];
    respondentes: number;
    table: AnalyticsTable;
    exportFilename: string;
    timeZone: string;
    wide?: boolean;
}

const percent = (v: unknown) => (typeof v === 'number' ? formatPct(v) : '-');
const respondentesLabel = (n: number) => `${formatInt(n)} ${n === 1 ? 'conta respondente' : 'contas respondentes'}`;

/** Barras horizontais com a contagem de respondentes em cada resposta. */
export function DistributionChart({
    title,
    infoKey,
    bars,
    respondentes,
    table,
    exportFilename,
    timeZone,
    wide,
}: DistributionChartProps) {
    return (
        <ChartCard
            title={title}
            infoKey={infoKey}
            subtitle={respondentesLabel(respondentes)}
            table={table}
            formatters={{ pct: percent }}
            exportFilename={exportFilename}
            timeZone={timeZone}
            wide={wide}
        >
            <div role="img" aria-label={title} style={{ height: bars.length * ROW_HEIGHT + AXIS_HEIGHT }}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={bars} layout="vertical" margin={{ top: 4, right: 32, bottom: 0, left: 0 }} barCategoryGap={8}>
                        <CartesianGrid horizontal={false} stroke="var(--color-gray-200)" />
                        <XAxis
                            type="number"
                            allowDecimals={false}
                            tickLine={false}
                            axisLine={false}
                            tick={{ fontSize: 11, fill: 'var(--color-gray-500)' }}
                        />
                        <YAxis
                            type="category"
                            dataKey="label"
                            width={wide ? 300 : 170}
                            interval={0}
                            tickLine={false}
                            axisLine={false}
                            tick={{ fontSize: 11, fill: 'var(--color-gray-700)' }}
                        />
                        <Tooltip
                            cursor={{ fill: 'var(--color-gray-50)' }}
                            formatter={(v, _n, item) => [
                                `${formatInt(typeof v === 'number' ? v : null)} contas, ${formatPct(item?.payload?.pct ?? null)} das respondentes`,
                                'Contas',
                            ]}
                            contentStyle={TOOLTIP_STYLE}
                        />
                        <Bar dataKey="count" fill="var(--color-primary-600)" radius={[0, 4, 4, 0]} isAnimationActive={false}>
                            <LabelList dataKey="count" position="right" fontSize={11} fill="var(--color-gray-700)" />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </ChartCard>
    );
}

interface GroupChartProps {
    title: string;
    infoKey: MetricInfoKey;
    series: GroupSeries[];
    rows: GroupRow[];
    respondentes: number;
    table: AnalyticsTable;
    exportFilename: string;
    timeZone: string;
}

/** Barras agrupadas em percentual do grupo (ex.: Setor x comportamento). */
export function GroupChart({ title, infoKey, series, rows, respondentes, table, exportFilename, timeZone }: GroupChartProps) {
    const data = rows.map((r) => ({ group: r.group, respondentes: r.respondentes, counts: r.counts, ...r.values }));
    const formatters = Object.fromEntries(table.columns.filter((c) => c.key.endsWith('Pct')).map((c) => [c.key, percent]));
    return (
        <ChartCard
            title={title}
            infoKey={infoKey}
            subtitle={respondentesLabel(respondentes)}
            table={table}
            formatters={formatters}
            exportFilename={exportFilename}
            timeZone={timeZone}
        >
            <div role="img" aria-label={title} style={{ height: rows.length * series.length * 18 + rows.length * 16 + AXIS_HEIGHT + 32 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data} layout="vertical" margin={{ top: 4, right: 40, bottom: 0, left: 0 }} barCategoryGap={10}>
                        <CartesianGrid horizontal={false} stroke="var(--color-gray-200)" />
                        <XAxis
                            type="number"
                            domain={[0, 100]}
                            ticks={[0, 25, 50, 75, 100]}
                            tickFormatter={(v) => `${v}%`}
                            tickLine={false}
                            axisLine={false}
                            tick={{ fontSize: 11, fill: 'var(--color-gray-500)' }}
                        />
                        <YAxis
                            type="category"
                            dataKey="group"
                            width={150}
                            interval={0}
                            tickLine={false}
                            axisLine={false}
                            tick={{ fontSize: 11, fill: 'var(--color-gray-700)' }}
                        />
                        <Tooltip
                            cursor={{ fill: 'var(--color-gray-50)' }}
                            formatter={(v, name, item) => {
                                const s = series.find((x) => x.label === name);
                                const p = item?.payload as { respondentes: number; counts: Record<string, number> } | undefined;
                                const n = s && p ? p.counts[s.key] ?? 0 : null;
                                const of = p ? ` (${formatInt(n)} de ${formatInt(p.respondentes)} contas)` : '';
                                return [`${formatPct(typeof v === 'number' ? v : null)}${of}`, name];
                            }}
                            contentStyle={TOOLTIP_STYLE}
                        />
                        <Legend iconType="square" wrapperStyle={{ fontSize: 11 }} />
                        {series.map((s, i) => (
                            <Bar
                                key={s.key}
                                dataKey={s.key}
                                name={s.label}
                                fill={SERIES_COLORS[i % SERIES_COLORS.length]}
                                radius={[0, 4, 4, 0]}
                                isAnimationActive={false}
                            >
                                <LabelList
                                    dataKey={s.key}
                                    position="right"
                                    fontSize={10}
                                    fill="var(--color-gray-700)"
                                    formatter={(v: unknown) => (typeof v === 'number' ? formatPct(v) : '')}
                                />
                            </Bar>
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </ChartCard>
    );
}
